import {
  Document,
  CreateDocumentRequest,
  UpdateDocumentRequest,
  DocumentFilters,
  GetDocumentsResponse,
  UploadUrlRequest,
  UploadUrlResponse,
  DownloadUrlResponse,
  ProcessDocumentResponse,
  CompleteSignatureRequest,
  CompleteSignatureResponse,
  DocumentHistoryResponse
} from '../types/documents';

const API_BASE_URL = import.meta.env.VITE_API_URL;

class DocumentsApi {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = localStorage.getItem('authToken') || 'mock-jwt-token-admin';
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async getUploadUrl(data: UploadUrlRequest): Promise<UploadUrlResponse> {
    return this.request('/documents/upload-url', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createDocument(data: CreateDocumentRequest): Promise<Document> {
    return this.request('/documents', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getDocuments(params: DocumentFilters = {}): Promise<GetDocumentsResponse> {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, value]) => value != null && value !== '')
    ).toString();
    
    return this.request(`/documents${queryString ? `?${queryString}` : ''}`);
  }

  async getDocument(documentId: string): Promise<Document> {
    return this.request(`/documents/${documentId}`);
  }

  async updateDocument(documentId: string, data: UpdateDocumentRequest): Promise<Document> {
    return this.request(`/documents/${documentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteDocument(documentId: string): Promise<void> {
    await this.request(`/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  async getDownloadUrl(documentId: string): Promise<DownloadUrlResponse> {
    return this.request(`/documents/${documentId}/download`);
  }

  async processDocument(documentId: string): Promise<ProcessDocumentResponse> {
    return this.request(`/documents/${documentId}/process`, {
      method: 'POST',
    });
  }

  async completeSignature(documentId: string, signatureData: CompleteSignatureRequest): Promise<CompleteSignatureResponse> {
    return this.request(`/documents/${documentId}/signature/complete`, {
      method: 'POST',
      body: JSON.stringify(signatureData),
    });
  }

  async getDocumentHistory(documentId: string): Promise<DocumentHistoryResponse> {
    return this.request(`/documents/${documentId}/history`);
  }
}

export const documentsApi = new DocumentsApi();
export default documentsApi;